import { apiFetch } from '@/lib/apiFetch';
import { nactiPdfMake } from '@/lib/tiskStrihaniA4';

export type NitKontrola = {
  kod: string;
  barva: string;
  /** Kolik předskladněných kusů nit potřebuje (sloupec „Potřeba (ks)"). */
  ksProduktu: string;
  minimum: string;
};

// Kontrolní list nití na A4 — dílna projde sklad a do širokého sloupce
// „Objednat" ručně dopíše, kolik cívek objednat. Jen nitě s nastaveným minimem.

/** Vyrobí PDF kontrolního listu jako Blob (k náhledu i do fronty Xeroxu). */
export async function pdfKontrolaNiti(nite: NitKontrola[]): Promise<Blob> {
  const pdfMake = await nactiPdfMake();
  const dnes = new Date();
  const datum = `${dnes.getDate()}. ${dnes.getMonth() + 1}. ${dnes.getFullYear()}`;
  // Minimum je nejdůležitější sloupec — podbarvený šedě (hlavička tmavší).
  // Světlejší než ~#d6d6d6 Xerox v dílně vytiskne jako bílou.
  const MINIMUM = 3;
  const STRED = new Set([0, 2, MINIMUM]);
  const hlavicka = ['Číslo', 'Barva', 'Ks produktů', 'Minimum', 'Objednat'].map((t, i) => ({
    text: t,
    bold: true,
    fillColor: i === MINIMUM ? '#bdbdbd' : '#eeeeee',
    alignment: STRED.has(i) ? 'center' : 'left',
  }));
  const radky = nite.map((n) => [
    { text: n.kod, bold: true, alignment: 'center' },
    n.barva,
    { text: n.ksProduktu || '0', alignment: 'center' },
    { text: `${n.minimum} ks`, alignment: 'center', bold: true, fillColor: '#d6d6d6' },
    '',
  ]);
  const doc = pdfMake.createPdf({
    pageSize: 'A4',
    pageMargins: [36, 36, 36, 36],
    defaultStyle: { font: 'Roboto', fontSize: 11 },
    content: [
      { text: 'Kontrola nití', fontSize: 18, bold: true },
      { text: `${datum} · ${nite.length} nití`, color: '#666666', margin: [0, 2, 0, 12] },
      {
        table: {
          headerRows: 1,
          widths: [50, '*', 70, 60, 150],
          body: [hlavicka, ...radky],
        },
        layout: {
          hLineColor: () => '#999999',
          vLineColor: () => '#999999',
          // pdfmake 0.2 neumí svislé zarovnání buněk — stejné odsazení nahoře
          // i dole drží jednořádkový text uprostřed řádku.
          paddingTop: (i: number) => (i === 0 ? 5 : 9),
          paddingBottom: (i: number) => (i === 0 ? 5 : 9),
        },
      },
    ],
  });
  return new Promise<Blob>((resolve) => doc.getBlob(resolve));
}

/** Pošle hotové PDF do tiskové fronty na Xerox v dílně (stejně jako plán stříhání). */
export async function odeslatNaXerox(pdf: Blob): Promise<void> {
  await apiFetch(`/api/print?title=${encodeURIComponent('Kontrola nití')}&printer=xerox`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/pdf' },
    body: pdf,
  });
}
